import React, { useState, memo, useEffect, useCallback } from 'react';
import { Avatar, Button, Col, Dropdown, List, message, Modal, Radio, Row, Spin, Tag, Tooltip } from 'antd';
import CustomDrawer from "components/shared-components/CustomDrawer";
import { ReloadOutlined, UserOutlined, SwapOutlined, MoreOutlined, ArrowLeftOutlined, SearchOutlined, HistoryOutlined } from '@ant-design/icons';
import apiClient from 'services/apiClient';
import { getColorForName, getInitials } from '../Conversation';
import Flex from 'components/shared-components/Flex';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { useMyContext } from 'Context/MyContextProvider';
import ProfileModel from '../OtherChatComps/ProfileModel';
import { Link, useNavigate } from 'react-router-dom';
import { ExportTimer } from '../OtherChatComps/ChatExpireTimer';
import { ChatMessageSearchTrigger } from '../ChatMessageSearchPopup';
import ChatMessageSearchPopup from '../ChatMessageSearchPopup';

const AGENTS_PAGE_SIZE = 15;

const ChatMenu = (props) => {
    const {
        activeAgent,
        visible,
        toggleModal,
        wa_id,
        displayPhoneNumber,
        customerName,
        authToken,
        api,
        getChats,
        onAssignSuccess,
    } = props;
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    const sortAgents = useCallback((list) => {
        const activeAgentId = activeAgent?.agent_id;
        if (!activeAgentId) {
            return list;
        }

        const current = list.find((agent) => agent?.id === activeAgentId);
        const rest = list.filter((agent) => agent?.id !== activeAgentId);

        return current ? [current, ...rest] : list;
    }, [activeAgent?.agent_id]);

    const fetchAgents = useCallback(async (nextPage = 1, append = false) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }

        try {
            const response = await apiClient.get(`${api}user/support-agents`, {
                params: { page: nextPage, per_page: AGENTS_PAGE_SIZE },
                headers: { Authorization: `Bearer ${authToken}` },
            });

            const payload = response.data || {};
            const fetched = Array.isArray(payload.data) ? payload.data : [];
            setAgents((prev) => {
                const merged = append ? [...prev, ...fetched] : fetched;
                const unique = Array.from(new Map(merged.map((item) => [item.id, item])).values());

                return sortAgents(unique);
            });
            setPage(payload.current_page || nextPage);
            setHasMore(Boolean(payload.has_more));
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to load support agents');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [api, authToken, sortAgents]);

    useEffect(() => {
        if (!visible) {
            return;
        }

        setSelectedAgent(activeAgent?.agent_id || null);
        fetchAgents(1, false);
    }, [visible, activeAgent?.agent_id, fetchAgents]);

    const onAssignChat = async () => {
        if (!selectedAgent) {
            message.warning('Please select a support agent');
            return;
        }

        setLoading(true);
        try {
            const response = await apiClient.post(
                `${api}support-agent/assign`,
                {
                    agent_id: selectedAgent,
                    display_phone_number: String(displayPhoneNumber ?? ''),
                    wa_id: String(wa_id ?? ''),
                    customer_name: customerName,
                },
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (response.data?.status) {
                message.success('Chat assigned successfully');
                onAssignSuccess?.(response.data?.data);
                getChats(wa_id);
                toggleModal();
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Error assigning chat');
        } finally {
            setLoading(false);
        }
    };

    const activeDays = (data) => data?.split(',').map((day) => day.trim()) || [];

    const modalTitle = activeAgent?.agent_id ? 'Reassign Chat' : 'Assign Chat To Support Agent';

    return (
        <CustomDrawer
            width={600}
            title={modalTitle}
            open={visible}
            onClose={toggleModal}
            maskClosable={false}
            keyboard={true}
            footer={
                <div className="text-right">
                    <Button onClick={toggleModal} style={{ marginRight: 8 }}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        loading={loading}
                        disabled={!selectedAgent}
                        onClick={onAssignChat}
                    >
                        {activeAgent?.agent_id ? 'Reassign' : 'Assign'}
                    </Button>
                </div>
            }
        >
            <div className="h-50 overflow-auto">
                {loading && agents.length === 0 ? (
                    <div className="d-flex justify-content-center py-4">
                        <Spin />
                    </div>
                ) : (
                    <>
                        <Radio.Group
                            value={selectedAgent}
                            onChange={(e) => setSelectedAgent(e.target.value)}
                            style={{ width: '100%' }}
                        >
                            {agents.map((agent) => (
                                <Row
                                    key={agent?.id}
                                    style={{
                                        borderRadius: '15px',
                                        background: selectedAgent === agent?.id ? 'rgba(227, 227, 227,1)' : undefined,
                                        padding: '12px 10px',
                                        borderBottom: '1px solid #f0f0f0',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Col flex="auto">
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Avatar
                                                style={{
                                                    backgroundColor: getColorForName(agent?.name),
                                                    marginRight: 12,
                                                }}
                                            >
                                                {getInitials(agent?.name)}
                                            </Avatar>
                                            <Flex flexDirection="column" style={{ flexWrap: 'wrap', gap: '4px' }}>
                                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span
                                                        style={{
                                                            width: '10px',
                                                            height: '10px',
                                                            borderRadius: '50%',
                                                            backgroundColor: agent?.support_agent?.online === 1 ? 'green' : 'red',
                                                            marginRight: '5px',
                                                        }}
                                                    />
                                                    {agent?.name}
                                                    <span className="text-muted ml-1" style={{ fontSize: '12px' }}>
                                                        ({agent?.active_chat_count ?? 0} chats)
                                                    </span>
                                                </span>
                                                <Flex style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '100%' }}>
                                                    {activeDays(agent?.support_agent?.working_days)?.map((day) => (
                                                        <Tag
                                                            key={day}
                                                            color="blue"
                                                            style={{
                                                                borderRadius: '12px',
                                                                padding: '0 8px',
                                                                margin: 0,
                                                                fontSize: '11px',
                                                                lineHeight: '16px',
                                                            }}
                                                        >
                                                            {day}
                                                        </Tag>
                                                    ))}
                                                </Flex>
                                            </Flex>
                                        </div>
                                    </Col>
                                    <Col>
                                        <Radio value={agent?.id} />
                                    </Col>
                                </Row>
                            ))}
                        </Radio.Group>
                        {hasMore && (
                            <div className="d-flex justify-content-center py-3">
                                <Button loading={loadingMore} onClick={() => fetchAgents(page + 1, true)}>
                                    Load more
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </CustomDrawer>
    );
};

const ChatContentHeader = memo((props) => {
    const { isMobile, UserData, isSupportAgent } = useMyContext();
    const {
        cswExpiresAt,
        isExpired,
        name,
        number,
        onRefresh,
        activeAgent,
        wa_id,
        displayPhoneNumber,
        authToken,
        api,
        getChats,
        onAssignSuccess,
        messageSearch,
    } = props;
    const navigate = useNavigate();
    const [visible, setVisible] = useState(false);
    const [historyVisible, setHistoryVisible] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyItems, setHistoryItems] = useState([]);

    const assignedAgentName = activeAgent?.agent_name;
    const assignedAgentOnline = activeAgent?.online === 1;

    const toggleModal = () => {
        setVisible(!visible);
    };

    const fetchAssignmentHistory = useCallback(async () => {
        if (!wa_id || !displayPhoneNumber) {
            return;
        }

        setHistoryLoading(true);
        try {
            const response = await apiClient.get(`${api}conversation/assignment-history`, {
                params: {
                    wa_id: String(wa_id ?? ''),
                    display_phone_number: String(displayPhoneNumber ?? ''),
                },
                headers: {
                    Authorization: `Bearer ${authToken}`,
                },
            });

            setHistoryItems(Array.isArray(response.data?.data) ? response.data.data : []);
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to load assignment history');
        } finally {
            setHistoryLoading(false);
        }
    }, [api, authToken, displayPhoneNumber, wa_id]);

    const openAssignmentHistory = () => {
        setHistoryVisible(true);
        fetchAssignmentHistory();
    };

    const menuItems = [
        ...(messageSearch ? [{
            key: 'search',
            icon: <SearchOutlined />,
            label: 'Search in conversation',
            onClick: () => messageSearch.setOpen(true),
        }] : []),
        {
            key: 'refresh',
            icon: <ReloadOutlined />,
            label: 'Refresh Chat',
            onClick: onRefresh,
        },
        {
            key: 'assignment-history',
            icon: <HistoryOutlined />,
            label: 'Assignment History',
            onClick: openAssignmentHistory,
        },
        ...(!isSupportAgent && assignedAgentName ? [{
            key: 'agent',
            icon: <SwapOutlined />,
            label: `Agent: ${assignedAgentName} (${assignedAgentOnline ? 'Online' : 'Offline'})`,
            onClick: toggleModal,
        }] : []),
    ];

    const [profileModalVisible, setProfileModalVisible] = useState(false);
    const toggleProfileModal = () => {
        setProfileModalVisible(!profileModalVisible);
    };

    return (
        <>
            <ProfileModel
                name={name}
                api={api}
                token={authToken}
                parent={UserData?.whatsapp_number}
                number={number}
                show={profileModalVisible}
                toggleProfileModal={toggleProfileModal}
                isMobile={isMobile}
            />
            {!isSupportAgent && (
                <ChatMenu
                    activeAgent={activeAgent}
                    visible={visible}
                    toggleModal={toggleModal}
                    wa_id={wa_id}
                    api={api}
                    getChats={getChats}
                    authToken={authToken}
                    displayPhoneNumber={displayPhoneNumber}
                    customerName={name}
                    onAssignSuccess={onAssignSuccess}
                />
            )}
            <CustomDrawer
                title="Assignment History"
                open={historyVisible}
                onClose={() => setHistoryVisible(false)}
                width={520}
                maskClosable={true}
            >
                {historyLoading ? (
                    <div className="text-center py-4">
                        <Spin />
                    </div>
                ) : (
                    <List
                        dataSource={historyItems}
                        locale={{ emptyText: 'No assignment history yet' }}
                        renderItem={(item) => (
                            <List.Item>
                                <List.Item.Meta
                                    title={item.message_text}
                                    description={`${item.source_label || '—'} · ${item.created_at_label || ''}`}
                                />
                            </List.Item>
                        )}
                    />
                )}
            </CustomDrawer>
            <div className="chat-content-header">
                {messageSearch?.open && isMobile ? (
                    <div className="d-flex align-items-center w-100" style={{ gap: '10px' }}>
                        <ArrowLeftOutlined
                            className="cursor-pointer"
                            style={{ fontSize: '18px', flexShrink: 0 }}
                            onClick={messageSearch.closeSearch}
                        />
                        <div style={{ flexGrow: 1, minWidth: 0 }}>
                            <ChatMessageSearchPopup
                                open={messageSearch.open}
                                query={messageSearch.query}
                                onQueryChange={messageSearch.setQuery}
                                statusText={messageSearch.statusText}
                                onPrevious={messageSearch.goToPreviousMatch}
                                onNext={messageSearch.goToNextMatch}
                                onClose={messageSearch.closeSearch}
                                onSearchFull={messageSearch.searchFullConversation}
                                showSearchFull={messageSearch.showSearchFull}
                                searching={messageSearch.searchingRemote}
                                loadingContext={messageSearch.loadingContext}
                                canGoPrevious={messageSearch.canGoPrevious}
                                canGoNext={messageSearch.canGoNext}
                                fullWidth
                            />
                        </div>
                    </div>
                ) : (
                    <Row align="middle" gutter={ROW_GUTTER} className="w-100">
                        <Col xs={18} sm={18} md={16} lg={16} xl={16}>
                            <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                                {isMobile && (
                                    <ArrowLeftOutlined
                                        className="cursor-pointer"
                                        style={{ fontSize: '18px', flexShrink: 0, marginRight: 10 }}
                                        onClick={() => navigate('/chats')}
                                    />
                                )}
                                <Link
                                    className="profile-name d-flex align-items-center"
                                    onClick={toggleProfileModal}
                                    style={{ minWidth: 0, overflow: 'hidden' }}
                                >
                                    <Avatar
                                        size={isMobile ? 36 : 40}
                                        style={{ backgroundColor: getColorForName(name), flexShrink: 0, marginRight: 10 }}
                                    >
                                        {getInitials(name)}
                                    </Avatar>
                                    <div style={{ minWidth: 0 }}>
                                        <h4
                                            className="mb-0"
                                            style={{
                                                fontSize: isMobile ? '0.9rem' : undefined,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {isMobile && name?.length > 10 ? `${name.slice(0, 10)}...` : name}
                                        </h4>
                                        {!isMobile && (
                                            <p style={{ fontSize: '0.8rem' }} className="mb-0">{number}</p>
                                        )}
                                    </div>
                                </Link>
                                {!isMobile && !isSupportAgent && assignedAgentName && (
                                    <div className="d-flex align-items-center gap-2 ml-2">
                                        <SwapOutlined />
                                        <div className="cursor-pointer" onClick={toggleModal}>
                                            <strong>{assignedAgentName}</strong>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <span
                                                    style={{
                                                        width: '10px',
                                                        height: '10px',
                                                        borderRadius: '50%',
                                                        backgroundColor: assignedAgentOnline ? 'green' : 'red',
                                                        marginRight: '5px',
                                                    }}
                                                />
                                                {assignedAgentOnline ? 'Online' : 'Offline'}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Col>
                        <Col xs={6} sm={6} md={8} lg={8} xl={8}>
                            <div className="d-flex gap-2 justify-content-end align-items-center">
                                <ExportTimer cswExpiresAt={cswExpiresAt} isExpired={isExpired} isMobile={isMobile} />
                                {messageSearch && (
                                    <ChatMessageSearchTrigger
                                        open={messageSearch.open}
                                        onOpenChange={(nextVisible) => {
                                            if (isMobile) return;
                                            if (nextVisible) {
                                                messageSearch.setOpen(true);
                                                return;
                                            }
                                            messageSearch.closeSearch();
                                        }}
                                        query={messageSearch.query}
                                        onQueryChange={messageSearch.setQuery}
                                        statusText={messageSearch.statusText}
                                        onPrevious={messageSearch.goToPreviousMatch}
                                        onNext={messageSearch.goToNextMatch}
                                        onClose={messageSearch.closeSearch}
                                        onSearchFull={messageSearch.searchFullConversation}
                                        showSearchFull={messageSearch.showSearchFull}
                                        searching={messageSearch.searchingRemote}
                                        loadingContext={messageSearch.loadingContext}
                                        canGoPrevious={messageSearch.canGoPrevious}
                                        canGoNext={messageSearch.canGoNext}
                                    >
                                        <Tooltip title="Search in conversation">
                                            <SearchOutlined
                                                className="cursor-pointer"
                                                style={{ fontSize: 16 }}
                                                onClick={() => {
                                                    if (isMobile) {
                                                        messageSearch.setOpen(true);
                                                    }
                                                }}
                                            />
                                        </Tooltip>
                                    </ChatMessageSearchTrigger>
                                )}
                                {isMobile ? (
                                    <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
                                        <MoreOutlined className="cursor-pointer" style={{ fontSize: '18px' }} />
                                    </Dropdown>
                                ) : (
                                    <>
                                        <Tooltip title="Refresh Chat">
                                            <ReloadOutlined className="cursor-pointer" onClick={onRefresh} />
                                        </Tooltip>
                                        <Tooltip title="Assignment History">
                                            <HistoryOutlined className="cursor-pointer" onClick={openAssignmentHistory} />
                                        </Tooltip>
                                        {(!isSupportAgent && isMobile) && (
                                            <Tooltip title={activeAgent?.agent_id ? 'Reassign Chat' : 'Assign Chat'}>
                                                <UserOutlined className="cursor-pointer" onClick={toggleModal} />
                                            </Tooltip>
                                        )}
                                    </>
                                )}
                            </div>
                        </Col>
                    </Row>
                )}
            </div>
        </>
    );
});

export default ChatContentHeader;
